import Service from 'react-reservice';

import marketHideAPI, { EHideType } from '@webtrader/core/api/market/hide';
import { ESymbolType, ISymbol } from '@webtrader/core/api/market/symbols';
import symbolCategoriesAPI from '@webtrader/core/api/market/symbols/category';
import watchlistAPI, { EWatchlistFilter } from '@webtrader/core/api/market/watchlist';
import { EMarketFilter } from '@webtrader/core/constants/symbol';
import { symbolTypeToMarketFilter } from '@webtrader/core/format/symbol';
import { orderService } from '@webtrader/core/services/order';
import { symbolsService } from '@webtrader/core/services/symbols';

interface IMarketContext {
  topPickListLoading: boolean;
  topGainerListLoading: boolean;
  topLoserListLoading: boolean;
  marketCategoryLoading: boolean;
  topPickList: ISymbol[];
  topGainerList: ISymbol[];
  topLoserList: ISymbol[];
  topMarketType?: EWatchlistFilter;
  symbolCategories: ESymbolType[];
}

interface ISaveMarketPreferenceParams {
  showAll: boolean;
  type: EHideType;
  values: string[];
}

const defaultContext: IMarketContext = {
  topPickListLoading: false,
  topGainerListLoading: false,
  topLoserListLoading: false,
  marketCategoryLoading: true,
  topPickList: [],
  topGainerList: [],
  topLoserList: [],
  topMarketType: undefined,
  symbolCategories: []
};

class MarketService extends Service<IMarketContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public onSaveMarketPreference = async (params: ISaveMarketPreferenceParams) => {
    const { showAll, type, values } = params;

    try {
      await marketHideAPI.hide({ showAll, type, values });
    } catch {}
  };

  public initMarketSymbolCategories = async (needPreset?: boolean) => {
    try {
      this.context.marketCategoryLoading = true;
      const { result } = await symbolCategoriesAPI();

      this.context.symbolCategories = result?.symbolTypeList || [];
      const filteredSymbolCategories = this.context.symbolCategories.filter((symbol) => symbol !== ESymbolType.OTHERS);
      if (filteredSymbolCategories.length > 0) {
        if (needPreset) {
          const firstItem = filteredSymbolCategories[0];
          const type = symbolTypeToMarketFilter(firstItem);
          symbolsService.setSelectedMarketFilter(type);
        }
      } else {
        symbolsService.setSelectedMarketFilter(EMarketFilter.FAVOURITES);
      }
    } catch {
    } finally {
      this.context.marketCategoryLoading = false;
    }
  };

  public initMarketTopListings = async () => {
    try {
      this.context.topPickListLoading = true;
      this.context.topGainerListLoading = true;
      this.context.topLoserListLoading = true;

      const [
        { result: topPickList = [] },
        { result: topGainerList = [] },
        { result: topLoserList = [] }
      ] = await Promise.all([
        watchlistAPI.category({ code: EWatchlistFilter.TOP_PICK }).catch((e) => e),
        watchlistAPI.category({ code: EWatchlistFilter.TOP_GAINER }).catch((e) => e),
        watchlistAPI.category({ code: EWatchlistFilter.TOP_LOSER }).catch((e) => e)
      ]);

      this.context.topPickList = topPickList.slice(0, 5);
      this.context.topGainerList = topGainerList.slice(0, 5);
      this.context.topLoserList = topLoserList.slice(0, 5);
    } catch {
    } finally {
      this.context.topPickListLoading = false;
      this.context.topGainerListLoading = false;
      this.context.topLoserListLoading = false;
    }
  };

  public resetMarketTopListings = () => {
    this.context.topPickList = [];
    this.context.topGainerList = [];
    this.context.topLoserList = [];
  };

  public setTopMarketType = (marketType: EWatchlistFilter) => {
    this.context.topMarketType = marketType;
  };

  public updateAllSymbolListForWatch = (symbol: string, watch: boolean) => {
    const { topPickList, topGainerList, topLoserList } = this.context;

    orderService.updateTopListingForWatch(symbol, watch);

    this.context.topPickList = topPickList.map((s) => {
      if (s.symbol === symbol) {
        return {
          ...s,
          watch
        };
      }

      return s;
    });

    this.context.topGainerList = topGainerList.map((s) => {
      if (s.symbol === symbol) {
        return {
          ...s,
          watch
        };
      }

      return s;
    });

    this.context.topLoserList = topLoserList.map((s) => {
      if (s.symbol === symbol) {
        return {
          ...s,
          watch
        };
      }

      return s;
    });
  };
}

export default new MarketService();
